import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, ShieldCheck, Phone, MessageCircle, ArrowRight, Clock, CheckCircle2,
} from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_media-insights-26/artifacts/jyhdvnlr_logo-png-cuadrado.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_LINK = "https://api.whatsapp.com/send?phone=34630666734&text=Hola%2C%20tengo%20una%20duda%20sobre%20el%20colch%C3%B3n%20Core";
const STORE_PHONE_TEL = "+34910889962";
const STORE_PHONE_DISPLAY = "910 88 99 62";

export default function Dudas() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", message: "", consent: false });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "¿Tienes dudas? Te llamamos · Core Colchón";
    window.scrollTo(0, 0);
  }, []);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Debes aceptar la política de privacidad para continuar.");
      return;
    }
    if (!/^[+\d\s().-]{6,}$/.test(form.phone)) {
      toast.error("Introduce un teléfono válido para que el asesor pueda llamarte.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads/callback`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        message: form.message.trim() || null,
        consent: form.consent,
      });
      if (typeof window !== "undefined" && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: "callback_request",
          lead_source: "dudas",
          lead_id: res.data?.id,
        });
      }
      // Meta Pixel — Lead event (browser) — shares event_id with server CAPI
      if (typeof window !== "undefined" && typeof window.fbq === "function" && res.data?.id) {
        window.fbq("track", "Lead", {
          content_name: "Solicitud de llamada — Dudas",
          content_category: "dudas",
        }, { eventID: res.data.id });
      }
      navigate("/gracias", { state: { leadId: res.data?.id, leadName: res.data?.name, source: "dudas" } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No hemos podido enviar tu solicitud. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="dudas-page" className="min-h-screen bg-[#F9F8FA]">
      {/* Header */}
      <header className="bg-white border-b border-[#E3DDE8]">
        <div className="max-w-5xl mx-auto px-5 sm:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="dudas-back-top">
            <img src={LOGO} alt="Core Colchón" className="h-9 w-9 object-contain"/>
            <div>
              <div className="font-display font-bold text-[#2E2638] leading-none">CORE COLCHÓN</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8B1538] font-bold">Hecho en España</div>
            </div>
          </Link>
          <Link to="/" data-testid="dudas-back-link" className="text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 text-[#5C5366] hover:text-[#8B1538] transition-colors">
            <ArrowLeft size={14}/> Volver a la web
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-10 py-12 sm:py-16">
        <div className="grid lg:grid-cols-12 gap-10 items-start">

          {/* Left col: copy + benefits */}
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B1538] mb-3">
              Atención personalizada
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2E2638] leading-tight">
              ¿Tienes dudas?<br/>
              <span className="text-[#8B1538]">Te llamamos.</span>
            </h1>
            <p className="mt-6 text-lg text-[#5C5366] max-w-xl leading-relaxed">
              Antes de venir a la tienda, un asesor te llama y resuelve <strong className="text-[#2E2638]">todas tus dudas</strong>: medidas, firmeza, materiales, garantía, financiación… lo que necesites.
            </p>
            <p className="mt-3 text-base text-[#5C5366] max-w-xl">
              Sin compromiso. Solo necesitamos tu nombre y un teléfono donde encontrarte.
            </p>

            <ul className="mt-10 space-y-4">
              {[
                { icon: Clock, t: "Te llamamos en menos de 24 h", d: "Dentro del horario de tienda (10–14h y 17–20:30h)." },
                { icon: ShieldCheck, t: "Sin compromiso de compra", d: "Resolvemos tus dudas tranquilamente, sin presión." },
                { icon: CheckCircle2, t: "Hablas con un asesor real", d: "Nada de bots ni call-centers externos. Equipo Core de Navalcarnero." },
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-4">
                  <div className="shrink-0 w-10 h-10 bg-[#F9F0F3] border border-[#E3DDE8] flex items-center justify-center text-[#8B1538]">
                    <b.icon size={18}/>
                  </div>
                  <div>
                    <div className="font-bold text-[#2E2638]">{b.t}</div>
                    <div className="text-sm text-[#5C5366] mt-0.5">{b.d}</div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-10 pt-7 border-t border-[#E3DDE8]">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#5C5366] mb-3">
                O contacta tú directamente
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="dudas-whatsapp"
                  className="px-5 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-wider text-xs flex items-center gap-2 transition-all hover:-translate-y-0.5 shadow-[0_4px_14px_rgba(5,150,105,0.35)]"
                >
                  <MessageCircle size={16}/> WhatsApp
                </a>
                <a
                  href={`tel:${STORE_PHONE_TEL}`}
                  data-testid="dudas-call"
                  className="px-5 py-3 bg-[#2E2638] hover:bg-[#1d1726] text-white font-bold uppercase tracking-wider text-xs flex items-center gap-2 transition-all hover:-translate-y-0.5"
                >
                  <Phone size={16}/> {STORE_PHONE_DISPLAY}
                </a>
              </div>
            </div>
          </div>

          {/* Right col: form */}
          <div className="lg:col-span-5">
            <form
              onSubmit={submit}
              className="bg-white border border-[#E3DDE8] p-7 sm:p-8 space-y-4 shadow-[0_8px_30px_rgba(46,38,56,0.08)] lg:sticky lg:top-8"
              data-testid="dudas-form"
            >
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#8B1538] mb-1.5">
                  Solicitud de llamada
                </div>
                <h2 className="font-display text-2xl font-bold text-[#2E2638] leading-tight">
                  Déjanos tu teléfono y un asesor te llama.
                </h2>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#5C5366] font-bold">Nombre</label>
                <input
                  data-testid="dudas-input-name"
                  type="text"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Tu nombre"
                  required
                  className="w-full mt-1 px-3 py-3 border border-[#E3DDE8] focus:border-[#8B1538] outline-none text-sm bg-transparent"
                />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#5C5366] font-bold">Teléfono</label>
                <input
                  data-testid="dudas-input-phone"
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="+34 600 000 000"
                  required
                  className="w-full mt-1 px-3 py-3 border border-[#E3DDE8] focus:border-[#8B1538] outline-none text-sm bg-transparent"
                />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#5C5366] font-bold">Tu duda (opcional)</label>
                <textarea
                  data-testid="dudas-input-message"
                  value={form.message}
                  onChange={set("message")}
                  rows={3}
                  placeholder="Si quieres, cuéntanos brevemente qué quieres consultar."
                  className="w-full mt-1 px-3 py-3 border border-[#E3DDE8] focus:border-[#8B1538] outline-none text-sm bg-transparent resize-none"
                />
              </div>

              <label className="flex items-start gap-2.5 text-xs text-[#5C5366] cursor-pointer pt-1">
                <input
                  data-testid="dudas-consent"
                  type="checkbox"
                  checked={form.consent}
                  onChange={set("consent")}
                  className="mt-0.5 w-4 h-4 accent-[#8B1538] shrink-0"
                />
                <span>
                  Acepto que Core Colchón me contacte y la <Link to="/privacidad" target="_blank" className="underline">política de privacidad</Link> (RGPD).
                </span>
              </label>

              <button
                type="submit"
                data-testid="dudas-submit"
                disabled={loading}
                className="group w-full mt-2 px-6 py-4 bg-[#8B1538] hover:bg-[#6A0F2A] disabled:opacity-60 text-white font-bold uppercase tracking-wider text-sm transition-all shadow-[0_4px_14px_rgba(139,21,56,0.39)] hover:shadow-[0_8px_24px_rgba(139,21,56,0.45)] hover:-translate-y-0.5 flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 size={18} className="animate-spin"/> : null}
                Quiero que me llamen
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1"/>
              </button>

              <p className="text-[11px] text-[#5C5366] flex items-center justify-center gap-1.5 mt-1">
                <ShieldCheck size={11} className="text-[#8B1538]"/> 100 % gratis y sin compromiso.
              </p>
            </form>
          </div>

        </div>
      </main>
    </div>
  );
}
